import { useState } from "react";
import { FileText, Loader2 } from "lucide-react";
import { notify } from "@/lib/notify";
import { generateCartographyPdf } from "@/lib/cartographyPdf";

/**
 * Cartography360PDFButton — Export PDF multi-pages brandé Noam pour
 * une Cartographie 360°. Reprend la logique nominative de PitchCaptureButton
 * (nom de client optionnel, mémorisé dans localStorage).
 *
 * Le PDF généré comporte : couverture · résumé exécutif · position fractale ·
 * relations · frictions · plans · fantômes · méthodologie. Chaque page porte
 * le fil rouge du sujet et le tampon « Préparé pour X » s'il est fourni.
 */
export default function Cartography360PDFButton({ data, filename = "noam-cartographie" }) {
  const [busy, setBusy] = useState(false);
  const [askClient, setAskClient] = useState(false);
  const [clientName, setClientName] = useState(() => {
    try { return localStorage.getItem("noam.carto360.clientName") || ""; } catch { return ""; }
  });

  const generate = (nameOverride) => {
    setAskClient(false);
    setBusy(true);
    const name = (nameOverride ?? clientName ?? "").trim();
    try { localStorage.setItem("noam.carto360.clientName", name); } catch { /* storage unavailable */ }
    try {
      generateCartographyPdf(data, { clientName: name, filename });
      notify.success(name
        ? `PDF cartographie prêt — préparé pour ${name}.`
        : "PDF cartographie prêt à envoyer.");
    } catch (e) {
      notify.error("Génération PDF impossible.");
      console.error("[cartography-pdf] generation failed", e);
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <button
        onClick={() => setAskClient(true)}
        disabled={busy || !data}
        className="text-[11px] px-2.5 py-1.5 rounded border border-[#EF9F27]/40 text-[#EF9F27] hover:bg-[#EF9F27]/10 inline-flex items-center gap-1.5 disabled:opacity-50 transition-colors"
        data-testid="carto360-pdf-btn"
        title="Export PDF multi-pages brandé Noam (couverture, cartographie, fantômes, plans, annexe)"
      >
        {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <FileText className="w-3.5 h-3.5" />}
        PDF complet
      </button>

      {askClient && (
        <div
          className="fixed inset-0 z-[100] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => setAskClient(false)}
          data-testid="carto360-pdf-client-overlay"
        >
          <div
            className="w-full max-w-sm rounded-lg border border-white/10 bg-[#0A0A0F] p-5 space-y-4 shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div>
              <div className="text-[10px] uppercase tracking-wider text-[#EF9F27] font-semibold">PDF cartographie complet</div>
              <div className="text-sm text-white mt-1 font-semibold">Pour qui préparez-vous ce dossier&nbsp;?</div>
              <p className="text-[11px] text-zinc-400 mt-1">Optionnel — le nom apparaîtra sur la couverture et le pied de page de chaque page.</p>
            </div>
            <input
              type="text"
              value={clientName}
              onChange={(e) => setClientName(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") generate(clientName); if (e.key === "Escape") setAskClient(false); }}
              autoFocus
              placeholder="Ex. Banque Cantonale Vaudoise"
              className="w-full text-sm px-3 py-2 rounded border border-white/15 bg-white/5 text-white placeholder-zinc-500 focus:outline-none focus:border-[#EF9F27]/60"
              data-testid="carto360-pdf-client-input"
              maxLength={80}
            />
            <div className="flex items-center justify-between gap-2">
              <button
                onClick={() => generate("")}
                className="text-[11px] text-zinc-400 hover:text-white underline underline-offset-2"
                data-testid="carto360-pdf-skip-btn"
              >
                Générer sans nom
              </button>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setAskClient(false)}
                  className="text-[11px] px-3 py-1.5 rounded border border-white/15 text-zinc-300 hover:bg-white/5"
                  data-testid="carto360-pdf-cancel-btn"
                >
                  Annuler
                </button>
                <button
                  onClick={() => generate(clientName)}
                  className="text-[11px] px-3 py-1.5 rounded bg-[#EF9F27] text-black font-semibold hover:bg-[#EF9F27]/90"
                  data-testid="carto360-pdf-confirm-btn"
                >
                  Générer le PDF
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
